import { OperationLog, OperationLogStatus } from '@/client/operation-log';
import { CsvImportIssue } from '@/pages/accounts/account-errors/error-log.helpers';
import {
  ActionRow,
  Banner,
  BannerIcon,
  BannerText,
  BannerTitle,
  CategoryMeta,
  CategoryTitle,
  EmptyStateCard,
  EmptyStateContent,
  ErrorCategory,
  ErrorTable,
  RowErrorCell,
  SectionTitle,
  TableLog,
} from '@/pages/accounts/account-errors/error-log.styled';
import { FlexContainer } from '@/ui/styled-ui';
import React, { useMemo } from 'react';

type Props = {
  log: OperationLog | undefined;
  renderFileUpload: (props: any) => React.ReactNode;
  t: (key: string, params?: Record<string, unknown>) => string;
};

export const CsvErrorsSection: React.FC<Props> = ({
  log,
  renderFileUpload,
  t,
}) => {
  const issues = (log?.errors ?? []) as CsvImportIssue[];
  const hasIssues =
    log && log.status !== OperationLogStatus.SUCCESS && issues.length > 0;

  const groupedByColumn = useMemo(() => {
    const groups: Record<
      string,
      { row: number | string; messages: string[] }[]
    > = {};

    issues.forEach((issue) => {
      const allFields = { ...issue.errors, ...issue.warnings };

      Object.entries(allFields).forEach(([column, messages]) => {
        const trimmedColumn = column?.trim() || t('errorLog.unknownColumn');

        if (!groups[trimmedColumn]) {
          groups[trimmedColumn] = [];
        }

        groups[trimmedColumn].push({
          row: issue.rowNumber,
          messages: Array.isArray(messages) ? messages : [String(messages)],
        });
      });
    });

    return groups;
  }, [issues, t]);

  return (
    <FlexContainer direction="column" gap={12} align="flex-start">
      <SectionTitle>{t('errorLog.csvErrors')}</SectionTitle>

      {!hasIssues && (
        <>
          <Banner variant="success">
            <BannerIcon variant="success">
              <i className="pi pi-check" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>{t('errorLog.success')}</BannerTitle>
            </BannerText>
          </Banner>
          <EmptyStateCard>
            <EmptyStateContent>
              <i className="pi pi-check-circle" />
              <span>{t('errorLog.noCurrentErrors')}</span>
            </EmptyStateContent>
          </EmptyStateCard>
        </>
      )}

      {hasIssues && (
        <>
          <Banner variant="warning">
            <BannerIcon variant="warning">
              <i className="pi pi-exclamation-circle" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>
                {t('errorLog.csvCompleted', { count: log.errorCount })}
              </BannerTitle>
              <span>{t('errorLog.csvReuploadHint')}</span>
            </BannerText>
          </Banner>

          <TableLog>
            {Object.entries(groupedByColumn).map(([column, rows]) => {
              const sortedRows = [...rows].sort((a, b) => {
                const first = Number(a.row);
                const second = Number(b.row);

                if (!Number.isNaN(first) && !Number.isNaN(second)) {
                  return first - second;
                }

                return String(a.row).localeCompare(String(b.row));
              });

              return (
                <ErrorCategory key={column}>
                  <FlexContainer justify="space-between" align="center">
                    <CategoryTitle>
                      {t('errorLog.columnLabel', { column })}
                    </CategoryTitle>
                    <CategoryMeta>
                      {t('errorLog.items', { count: rows.length })}
                    </CategoryMeta>
                  </FlexContainer>
                  <ErrorTable>
                    <thead>
                      <tr>
                        <th>{t('errorLog.row')}</th>
                        <th>{t('errorLog.error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row, index) => (
                        <tr key={`${row.row}-${index}`}>
                          <td>
                            {t('errorLog.row')} {row.row}
                          </td>
                          <td>
                            {row.messages.map((message, msgIndex) => (
                              <RowErrorCell key={msgIndex}>
                                {message}
                              </RowErrorCell>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ErrorTable>
                </ErrorCategory>
              );
            })}
          </TableLog>
        </>
      )}

      <ActionRow>
        {renderFileUpload({
          className: 'button',
          chooseLabel: t('errorLog.reupload'),
          chooseIcon: <i className="pi pi-refresh" />,
        })}
      </ActionRow>
    </FlexContainer>
  );
};
